// src/components/Header.js

import React, { useState, useEffect, useRef } from 'react';
import { 
  FiSearch, 
  FiUser, 
  FiLogOut, 
  FiSettings, 
  FiLock,
  FiChevronDown,
  FiMoon,
  FiSun,
  FiHelpCircle,
  FiGrid,
  FiAward,
  FiX,
  FiMapPin,
  FiCoffee,
  FiMenu,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// Session Configuration
const SESSION_CHECK_INTERVAL = 30000; // Check every 30 seconds
const SESSION_WARNING_TIME = 300000; // 5 minutes before expiry

// Location to route mapping
const LOCATION_ROUTES = {
  'african': '/ManagerDashboard_A',
  'continental': '/ManagerDashboard_C',
  'lounge': '/ManagerDashboard_L',
};

// Waiter location to route mapping
const WAITER_LOCATION_ROUTES = {
  'african': '/restaurant/african',
  'continental': '/restaurant/continental',
};

// Token utility functions
const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    // exp is in seconds, Date.now() is in milliseconds
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    return 0;
  }
};

const Header = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(0);
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    staffId: '',
    phone: '',
    location: '',
  });

  // Change Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: '',
    color: '',
  });
  
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const dialogRef = useRef(null);
  const locationRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const passwordDialogRef = useRef(null);

  // Token refresh function
  const refreshAuthToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${FULL_API_URL}/auth/refresh`, {
        refresh_token: refreshToken
      });

      if (response.data && response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        return response.data.access_token;
      }
      throw new Error('No access token in refresh response');
    } catch (err) {
      console.error('[Header] Token refresh failed:', err);
      // Clear all auth data and redirect to login
      localStorage.clear();
      navigate('/restaurantAuth');
      return null;
    }
  };

  // Load user data from localStorage
  useEffect(() => {
    const name = localStorage.getItem('userName') || localStorage.getItem('first_name') || 'User';
    const email = localStorage.getItem('user_email') || localStorage.getItem('email') || '';
    const role = localStorage.getItem('user_role') || localStorage.getItem('role') || 'Staff';
    const department = localStorage.getItem('department') || localStorage.getItem('userDepartment') || '';
    const staffId = localStorage.getItem('staff_id') || localStorage.getItem('staffId') || '';
    const phone = localStorage.getItem('phone') || localStorage.getItem('phoneNumber') || '';
    const location = localStorage.getItem('user_location') || localStorage.getItem('location') || '';

    const formattedLocation = location ? location.charAt(0).toUpperCase() + location.slice(1) : '';
    
    setUserData({
      name,
      email,
      role: role.charAt(0).toUpperCase() + role.slice(1),
      department,
      staffId,
      phone,
      location: formattedLocation,
    });
    
    // Set location from stored preference or user data
    const savedLocation = localStorage.getItem('selected_location') || location || '';
    setSelectedLocation(savedLocation ? savedLocation.charAt(0).toUpperCase() + savedLocation.slice(1) : '');
  }, []);

  // Fetch locations from API
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const token = localStorage.getItem('access_token');
      
      // Check if token is valid before making request
      if (!token || isTokenExpired(token)) {
        const newToken = await refreshAuthToken();
        if (!newToken) return;
      }

      const response = await axios.get(`${FULL_API_URL}/locations/dropdown`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setLocations(response.data);
      
      // If user has a location, set it as selected
      if (userData.location) {
        const matched = response.data.find(
          loc => loc.location.toLowerCase() === userData.location.toLowerCase()
        );
        if (matched) {
          setSelectedLocation(matched.location);
        }
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      // If unauthorized, try to refresh token and retry
      if (error.response && error.response.status === 401) {
        const newToken = await refreshAuthToken();
        if (newToken) {
          // Retry the request
          try {
            const response = await axios.get(`${FULL_API_URL}/locations/dropdown`, {
              headers: {
                'Authorization': `Bearer ${newToken}`
              }
            });
            setLocations(response.data);
          } catch (retryError) {
            console.error('Error fetching locations after refresh:', retryError);
          }
        }
      }
    } finally {
      setLoadingLocations(false);
    }
  };

  // Digital clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Session management - Check token expiry periodically
  useEffect(() => {
    const checkSession = () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setSessionWarning(false);
        setTimeUntilExpiry(0);
        return;
      }

      const expiryTime = getTokenExpiryTime(token);
      const currentTime = Date.now();
      const timeLeft = expiryTime - currentTime;

      setTimeUntilExpiry(timeLeft);

      if (timeLeft <= 0) {
        // Token expired - try to refresh
        refreshAuthToken().then(newToken => {
          if (!newToken) {
            // Refresh failed, redirect to login
            localStorage.clear();
            navigate('/restaurantAuth');
          }
        });
      } else if (timeLeft < SESSION_WARNING_TIME && timeLeft > 0) {
        setSessionWarning(true);
        // You could show a toast notification here
        console.warn(`Session expires in ${Math.floor(timeLeft / 60000)} minutes`);
      } else {
        setSessionWarning(false);
      }
    };

    // Check immediately
    checkSession();

    // Set up interval
    const interval = setInterval(checkSession, SESSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [navigate]);

  // Auto-refresh token when it's about to expire
  useEffect(() => {
    const autoRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();

      // If less than 2 minutes until expiry, refresh automatically
      if (timeLeft > 0 && timeLeft < 120000) {
        console.log('Auto-refreshing token...');
        await refreshAuthToken();
      }
    };

    // Check every minute
    const refreshInterval = setInterval(autoRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
        setShowSettings(false);
      }
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        // Don't close dialog on outside click
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when dialogs are open
  useEffect(() => {
    if (showLogoutDialog || showMobileMenu || showChangePasswordDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLogoutDialog, showMobileMenu, showChangePasswordDialog]);

  // Format time
  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatDate = (date) => {
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Navigation handlers
  const handleLogout = () => {
    setShowLogoutDialog(true);
    setShowProfileMenu(false);
    setShowMobileMenu(false);
  };

  const confirmLogout = () => {
    localStorage.clear();
    navigate('/restaurantAuth');
    setShowLogoutDialog(false);
    setShowMobileMenu(false);
  };

  const cancelLogout = () => {
    setShowLogoutDialog(false);
  };

  // Change Password handlers
  const handleChangePasswordClick = () => {
    setShowChangePasswordDialog(true);
    setShowProfileMenu(false);
    setShowMobileMenu(false);
    // Reset form
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordErrors({});
    setPasswordSuccess(false);
    setPasswordStrength({ score: 0, label: '', color: '' });
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Check password strength for new password
    if (field === 'newPassword' && value) {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    const strengths = [
      { score: 0, label: 'Very Weak', color: '#F44336' },
      { score: 1, label: 'Weak', color: '#FF9800' },
      { score: 2, label: 'Fair', color: '#FFC107' },
      { score: 3, label: 'Good', color: '#8BC34A' },
      { score: 4, label: 'Strong', color: '#4CAF50' },
      { score: 5, label: 'Very Strong', color: '#2E7D32' },
    ];

    const result = strengths.find(s => s.score === score) || strengths[0];
    return result;
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[a-z]/.test(passwordData.newPassword) || !/[A-Z]/.test(passwordData.newPassword)) {
      errors.newPassword = 'Password must contain both uppercase and lowercase letters';
    } else if (!/\d/.test(passwordData.newPassword)) {
      errors.newPassword = 'Password must contain at least one number';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is same as current (client-side hint)
    if (passwordData.currentPassword && passwordData.newPassword && 
        passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setIsChangingPassword(true);

    try {
      const token = localStorage.getItem('access_token');

      const response = await axios.post(`${FULL_API_URL}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_new_password: passwordData.confirmPassword,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      // Success
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowChangePasswordDialog(false);
        setPasswordSuccess(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }, 2000);
    } catch (err) {
      setPasswordErrors({
        general: err.response?.data?.detail || err.message || 'Failed to change password. Please try again.',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`#`);
      setShowMobileMenu(false);
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setShowLocationDropdown(false);
    setShowMobileMenu(false);
    
    // Store selected location
    localStorage.setItem('selected_location', location);
    
    // Navigate based on role and location
    const userRole = userData.role?.toLowerCase() || '';
    const locationKey = location.toLowerCase();
    
    console.log('Location selected:', location);
    console.log('User role:', userRole);
    
    if (userRole === 'manager') {
      // Manager routes
      const route = LOCATION_ROUTES[locationKey];
      if (route) {
        console.log('Navigating to manager route:', route);
        navigate(route);
      } else {
        console.warn('No manager route found for location:', location);
      }
    } else if (userRole === 'waiter') {
      // Waiter routes
      const route = WAITER_LOCATION_ROUTES[locationKey];
      if (route) {
        console.log('Navigating to waiter route:', route);
        navigate(route);
      } else {
        console.warn('No waiter route found for location:', location);
      }
    } else {
      console.log('User role not recognized for location-based navigation:', userRole);
    }
  };

  // Get role badge color
  const getRoleColor = (role) => {
    const roleColors = {
      'Admin': '#C62828',
      'Manager': '#E65100',
      'Waiter': '#2E7D32',
      'Cashier': '#1565C0',
      'Staff': '#6A1B9A',
      'HR': '#00838F',
      'Account': '#BF360C',
      'Store Keeper': '#4E342E',
    };
    return roleColors[role] || '#666';
  };

  // Get location badge color
  const getLocationColor = (location) => {
    const locationColors = {
      'Continental': '#1565C0',
      'African': '#E65100',
      'Lounge': '#6A1B9A',
    };
    return locationColors[location] || '#666';
  };

  const userInitial = userData.name ? userData.name.charAt(0).toUpperCase() : 'U';

  const isWaiter = userData.role?.toLowerCase() === 'waiter';
  const isManager = userData.role?.toLowerCase() === 'manager';
  const showLocation = (isWaiter || isManager) && userData.location;

  // Format time until expiry for display
  const formatTimeUntilExpiry = (ms) => {
    if (ms <= 0) return 'Expired';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <>
      <header className="epos-header">
        {/* Session Warning Banner */}
        {sessionWarning && timeUntilExpiry > 0 && timeUntilExpiry < SESSION_WARNING_TIME && (
          <div className="session-warning-banner">
            <span>⚠️ Session expires in {formatTimeUntilExpiry(timeUntilExpiry)}</span>
            <button onClick={() => refreshAuthToken()} className="session-renew-btn">
              Renew Session
            </button>
          </div>
        )}

        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label="Toggle menu"
        >
          <FiMenu size={24} />
        </button>

        {/* F&B Tagline (replaces search box) */}
        <div className="header-tagline">
          <span className="tagline-icon">🍽️</span>
          <div className="tagline-text">
            <span className="tagline-main">Food & Beverage</span>
            <span className="tagline-sub">Serving excellence, every order</span>
          </div>
        </div>

        {/* Center - Digital Clock & F&B Label */}
        <div className="header-center">
          <div className="digital-clock">
            <span className="clock-icon">⏰</span>
            <span className="clock-time">{formatTime(currentTime)}</span>
            <span className="clock-divider">|</span>
            <span className="clock-date">{formatDate(currentTime)}</span>
          </div>
          <div className="fnb-label">
            <FiCoffee size={16} className="fnb-icon" />
            <span className="fnb-text">F&B</span>
          </div>
        </div>

        {/* Right Section */}
        <div className="header-right">
          {/* Location Selection Dropdown - Visible on all devices */}
          <div className="location-select-wrapper" ref={locationRef}>
            <button 
              className="location-select-btn"
              onClick={() => setShowLocationDropdown(!showLocationDropdown)}
            >
              <div className="location-select-icon">
                <FiMapPin size={16} />
              </div>
              <span className="location-select-text">
                {selectedLocation || 'Select Location'}
              </span>
              <FiChevronDown size={16} className="location-select-arrow" />
            </button>

            {showLocationDropdown && (
              <div className="location-dropdown">
                <div className="location-dropdown-header">
                  <FiMapPin size={18} />
                  <span style={{ fontSize: '14px', fontWeight: '600' }}>Select Location</span>
                </div>
                <div className="location-dropdown-list">
                  {loadingLocations ? (
                    <div className="location-loading">Loading...</div>
                  ) : locations.length === 0 ? (
                    <div className="location-empty">No locations</div>
                  ) : (
                    locations.map((loc) => (
                      <button
                        key={loc.id}
                        className={`location-dropdown-item ${selectedLocation === loc.location ? 'active' : ''}`}
                        onClick={() => handleLocationSelect(loc.location)}
                      >
                        <span className="location-item-icon">
                          {selectedLocation === loc.location ? (
                            <span className="location-check">✓</span>
                          ) : (
                            <FiMapPin size={16} />
                          )}
                        </span>
                        <span className="location-item-name">{loc.location}</span>
                        {selectedLocation === loc.location && (
                          <span className="location-item-badge">Active</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Location Badge - Shows for Manager and Waiter */}
          {showLocation && (
            <div className="location-badge">
              <FiMapPin size={16} className="location-icon" />
              <span className="location-text">{userData.location}</span>
            </div>
          )}

          {/* Profile - Desktop */}
          <div 
            className="profile-wrapper desktop-only"
            ref={menuRef}
          >
            <div 
              className="profile"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="avatar-wrapper">
                <div className="avatar">{userInitial}</div>
                <div className="avatar-status online"></div>
              </div>
              <div className="profile-info">
                <div className="profile-name">{userData.name}</div>
                <div className="profile-role">
                  <span 
                    className="role-badge"
                    style={{ backgroundColor: getRoleColor(userData.role) }}
                  >
                    {userData.role}
                  </span>
                  {(isWaiter || isManager) && userData.location && (
                    <span 
                      className="location-badge-small"
                      style={{ backgroundColor: getLocationColor(userData.location) }}
                    >
                      <FiMapPin size={12} />
                      {userData.location}
                    </span>
                  )}
                </div>
              </div>
              <FiChevronDown size={18} className="profile-arrow" />
            </div>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div className="profile-dropdown">
                <div className="dropdown-user-info">
                  <div className="dropdown-avatar">{userInitial}</div>
                  <div>
                    <div className="dropdown-name">{userData.name}</div>
                    <div className="dropdown-email">{userData.email}</div>
                  </div>
                </div>

                {userData.department && (
                  <div className="dropdown-badge">
                    <FiGrid size={16} />
                    <span>{userData.department}</span>
                  </div>
                )}

                {(isWaiter || isManager) && userData.location && (
                  <div className="dropdown-badge">
                    <FiMapPin size={16} />
                    <span>Location: {userData.location}</span>
                  </div>
                )}

                {userData.staffId && (
                  <div className="dropdown-badge">
                    <FiAward size={16} />
                    <span>ID: {userData.staffId}</span>
                  </div>
                )}

                <div className="dropdown-divider"></div>

                <div 
                  className="dropdown-item"
                  onClick={handleChangePasswordClick}
                >
                  <FiLock size={18} />
                  <span>Change Password</span>
                </div>

                <div className="dropdown-divider"></div>

                <div 
                  className="dropdown-item logout"
                  onClick={handleLogout}
                >
                  <FiLogOut size={18} />
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Avatar */}
          <div className="mobile-avatar" onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div className="avatar small">{userInitial}</div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <>
          <div className="mobile-overlay" onClick={() => setShowMobileMenu(false)}></div>
          <div className="mobile-menu" ref={mobileMenuRef}>
            {/* Mobile User Info */}
            <div className="mobile-user-info">
              <div className="mobile-user-avatar">{userInitial}</div>
              <div className="mobile-user-details">
                <div className="mobile-user-name">{userData.name}</div>
                <div className="mobile-user-role">{userData.role}</div>
                {(isWaiter || isManager) && userData.location && (
                  <div className="mobile-user-location">
                    <FiMapPin size={14} />
                    <span>{userData.location}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mobile-menu-divider"></div>

            {/* Mobile Location Selection */}
            <div className="mobile-menu-section">
              <div className="mobile-menu-label">Location</div>
              <div className="mobile-location-list">
                {loadingLocations ? (
                  <div className="mobile-loading">Loading locations...</div>
                ) : locations.length === 0 ? (
                  <div className="mobile-empty">No locations available</div>
                ) : (
                  locations.map((loc) => (
                    <button
                      key={loc.id}
                      className={`mobile-location-item ${selectedLocation === loc.location ? 'active' : ''}`}
                      onClick={() => handleLocationSelect(loc.location)}
                    >
                      <FiMapPin size={18} />
                      <span>{loc.location}</span>
                      {selectedLocation === loc.location && (
                        <span className="mobile-location-check">✓</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="mobile-menu-divider"></div>

            {/* Mobile Menu Items */}
            <div className="mobile-menu-items">
              <button className="mobile-menu-item" onClick={handleChangePasswordClick}>
                <FiLock size={20} />
                <span>Change Password</span>
              </button>
              <button className="mobile-menu-item" onClick={() => setIsDarkMode(!isDarkMode)}>
                <span style={{ fontSize: '18px' }}>{isDarkMode ? '☀️' : '🌙'}</span>
                <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
              </button>
            </div>

            <div className="mobile-menu-divider"></div>

            {/* Mobile Logout */}
            <button className="mobile-logout-btn" onClick={handleLogout}>
              <FiLogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </>
      )}

      {/* Custom Logout Confirmation Dialog */}
      {showLogoutDialog && (
        <div className="dialog-overlay" onClick={cancelLogout}>
          <div className="dialog-container" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-content">
              <div className="dialog-icon-wrapper">
                <div className="dialog-icon">
                  <FiLogOut size={36} />
                </div>
              </div>
              
              <h3 className="dialog-title">Confirm Logout</h3>
              
              <p className="dialog-message">
                Are you sure you want to logout? You will need to login again to access your account.
              </p>

              <div className="dialog-user-info">
                <div className="dialog-user-avatar">{userInitial}</div>
                <div className="dialog-user-details">
                  <div className="dialog-user-name">{userData.name}</div>
                  <div className="dialog-user-email">{userData.email}</div>
                  {(isWaiter || isManager) && userData.location && (
                    <div className="dialog-user-location">
                      <FiMapPin size={14} />
                      <span>{userData.location}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="dialog-actions">
                <button 
                  className="dialog-btn dialog-btn-cancel"
                  onClick={cancelLogout}
                >
                  Cancel
                </button>
                <button 
                  className="dialog-btn dialog-btn-confirm"
                  onClick={confirmLogout}
                >
                  <FiLogOut size={18} />
                  Logout
                </button>
              </div>
            </div>

            <button className="dialog-close" onClick={cancelLogout}>
              <FiX size={24} />
            </button>
          </div>
        </div>
      )}

      {/* Change Password Dialog */}
      {showChangePasswordDialog && (
        <div className="dialog-overlay" onClick={() => setShowChangePasswordDialog(false)}>
          <div className="dialog-container password-dialog" ref={passwordDialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-content">
              <div className="dialog-icon-wrapper password-icon-wrapper">
                <div className="dialog-icon password-icon">
                  <FiLock size={32} />
                </div>
              </div>
              
              <h3 className="dialog-title">Change Password</h3>
              
              <p className="dialog-message">
                Enter your current password and choose a new password to update your account security.
              </p>

              {passwordErrors.general && (
                <div className="password-error-general">
                  <FiAlertCircle size={16} />
                  <span>{passwordErrors.general}</span>
                </div>
              )}

              {passwordSuccess && (
                <div className="password-success">
                  <FiCheckCircle size={18} />
                  <span>Password changed successfully! Redirecting...</span>
                </div>
              )}

              <div className="password-form">
                <div className="password-field">
                  <label className="password-label">Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.currentPassword ? 'error' : ''}`}
                      placeholder="Enter current password"
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <span className="password-error">{passwordErrors.currentPassword}</span>
                  )}
                </div>

                <div className="password-field">
                  <label className="password-label">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.newPassword ? 'error' : ''}`}
                      placeholder="Enter new password (min. 8 characters)"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <span className="password-error">{passwordErrors.newPassword}</span>
                  )}
                  
                  {/* Password Strength Indicator */}
                  {passwordData.newPassword && !passwordErrors.newPassword && (
                    <div className="password-strength">
                      <div className="strength-bar-container">
                        <div 
                          className="strength-bar"
                          style={{
                            width: `${(passwordStrength.score / 5) * 100}%`,
                            backgroundColor: passwordStrength.color,
                          }}
                        />
                      </div>
                      <span className="strength-label" style={{ color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="password-field">
                  <label className="password-label">Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm new password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <span className="password-error">{passwordErrors.confirmPassword}</span>
                  )}
                </div>

                <div className="password-requirements">
                  <div className={`requirement ${passwordData.newPassword.length >= 8 ? 'met' : ''}`}>
                    <span className="requirement-icon">
                      {passwordData.newPassword.length >= 8 ? '✓' : '○'}
                    </span>
                    <span>At least 8 characters</span>
                  </div>
                  <div className={`requirement ${/[a-z]/.test(passwordData.newPassword) && /[A-Z]/.test(passwordData.newPassword) ? 'met' : ''}`}>
                    <span className="requirement-icon">
                      {/[a-z]/.test(passwordData.newPassword) && /[A-Z]/.test(passwordData.newPassword) ? '✓' : '○'}
                    </span>
                    <span>Uppercase & lowercase letters</span>
                  </div>
                  <div className={`requirement ${/\d/.test(passwordData.newPassword) ? 'met' : ''}`}>
                    <span className="requirement-icon">
                      {/\d/.test(passwordData.newPassword) ? '✓' : '○'}
                    </span>
                    <span>At least one number</span>
                  </div>
                </div>
              </div>

              <div className="dialog-actions">
                <button 
                  className="dialog-btn dialog-btn-cancel"
                  onClick={() => {
                    setShowChangePasswordDialog(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                    setPasswordErrors({});
                    setPasswordSuccess(false);
                  }}
                  disabled={isChangingPassword}
                >
                  Cancel
                </button>
                <button 
                  className="dialog-btn dialog-btn-confirm password-confirm-btn"
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || passwordSuccess}
                >
                  {isChangingPassword ? (
                    <span className="loading-spinner" />
                  ) : (
                    <FiLock size={16} />
                  )}
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>

            <button className="dialog-close" onClick={() => {
              setShowChangePasswordDialog(false);
              setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
              });
              setPasswordErrors({});
              setPasswordSuccess(false);
            }}>
              <FiX size={24} />
            </button>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        /* Base Header */
        .epos-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-bottom: 2px solid rgba(0, 0, 0, 0.06);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.04);
          position: sticky;
          top: 0;
          z-index: 100;
          min-height: 68px;
          transition: all 0.3s ease;
          flex-wrap: wrap;
        }

        /* Session Warning Banner */
        .session-warning-banner {
          width: 100%;
          background: linear-gradient(135deg, #fff3e0, #ffe0b2);
          border-bottom: 2px solid #ff9800;
          padding: 8px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 14px;
          font-weight: 600;
          color: #e65100;
          border-radius: 6px;
          margin-bottom: 8px;
          animation: pulseWarning 2s ease-in-out infinite;
        }

        @keyframes pulseWarning {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }

        .session-renew-btn {
          background: #ff9800;
          color: white;
          border: none;
          padding: 4px 16px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 13px;
        }

        .session-renew-btn:hover {
          background: #f57c00;
          transform: scale(1.05);
        }

        /* Mobile Menu Toggle */
        .mobile-menu-toggle {
          display: none;
          background: none;
          border: none;
          color: #1a1a1a;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .mobile-menu-toggle:hover {
          background: #f1f3f5;
        }

        /* Header Tagline (replaces search box) */
        .header-tagline {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 16px;
          border-radius: 12px;
          background: linear-gradient(135deg, rgba(198, 40, 40, 0.06), rgba(198, 40, 40, 0.02));
          border: 1px solid rgba(198, 40, 40, 0.08);
          flex: 1;
          min-width: 240px;
          transition: all 0.3s ease;
        }

        .header-tagline:hover {
          background: linear-gradient(135deg, rgba(198, 40, 40, 0.1), rgba(198, 40, 40, 0.04));
          transform: translateY(-1px);
        }

        .tagline-icon {
          font-size: 20px;
          flex-shrink: 0;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
        }

        .tagline-text {
          display: flex;
          flex-direction: column;
          line-height: 1.25;
          overflow: hidden;
        }

        .tagline-main {
          font-size: 13px;
          font-weight: 700;
          color: #C62828;
          letter-spacing: 0.3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .tagline-sub {
          font-size: 11px;
          font-weight: 500;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Center */
        .header-center {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
        }

        .digital-clock {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 18px;
          border-radius: 10px;
          background: linear-gradient(135deg, rgba(198, 40, 40, 0.06), rgba(198, 40, 40, 0.02));
          border: 1px solid rgba(198, 40, 40, 0.08);
        }

        .clock-icon {
          font-size: 16px;
        }

        .clock-time {
          font-family: 'Courier New', monospace;
          font-size: 18px;
          font-weight: 700;
          color: #0a0a0a;
          letter-spacing: 0.5px;
        }

        .clock-divider {
          color: #94a3b8;
          font-size: 14px;
          opacity: 0.5;
        }

        .clock-date {
          font-size: 13px;
          font-weight: 500;
          color: #475569;
        }

        .fnb-label {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 5px 16px 5px 12px;
          border-radius: 20px;
          background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
          border: 1px solid rgba(46, 125, 50, 0.25);
          color: #1a4a2a;
          font-size: 13px;
          font-weight: 700;
          transition: all 0.3s ease;
          cursor: default;
          letter-spacing: 0.3px;
        }

        .fnb-label:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 12px rgba(46, 125, 50, 0.15);
        }

        .fnb-icon {
          flex-shrink: 0;
        }

        .fnb-text {
          white-space: nowrap;
        }

        /* Right Section */
        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }

        /* Location Select */
        .location-select-wrapper {
          position: relative;
        }

        .location-select-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px 6px 10px;
          border-radius: 20px;
          border: 1px solid rgba(46, 125, 50, 0.25);
          background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
          color: #1a4a2a;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .location-select-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 16px rgba(46, 125, 50, 0.2);
        }

        .location-select-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(46, 125, 50, 0.15);
          flex-shrink: 0;
        }

        .location-select-text {
          font-weight: 600;
          font-size: 13px;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #0a0a0a;
        }

        .location-select-arrow {
          transition: transform 0.3s ease;
          color: #1a4a2a;
        }

        .location-select-btn:hover .location-select-arrow {
          transform: rotate(180deg);
        }

        /* Location Dropdown */
        .location-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 220px;
          background: #ffffff;
          border-radius: 14px;
          box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.06);
          overflow: hidden;
          z-index: 1000;
          animation: slideDown 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .location-dropdown-header {
          padding: 12px 16px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 600;
          color: #0a0a0a;
          background: #f8f9fa;
        }

        .location-dropdown-list {
          max-height: 240px;
          overflow-y: auto;
          padding: 6px 0;
        }

        .location-dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 16px;
          border: none;
          background: transparent;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          font-weight: 500;
          color: #0a0a0a;
          text-align: left;
        }

        .location-dropdown-item:hover {
          background: #f1f3f5;
        }

        .location-dropdown-item.active {
          background: rgba(46, 125, 50, 0.08);
          color: #1a4a2a;
          font-weight: 600;
        }

        .location-item-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          color: #94a3b8;
        }

        .location-dropdown-item.active .location-item-icon {
          color: #2E7D32;
        }

        .location-check {
          color: #2E7D32;
          font-weight: 700;
          font-size: 16px;
        }

        .location-item-name {
          flex: 1;
          font-weight: 500;
        }

        .location-dropdown-item.active .location-item-name {
          font-weight: 600;
        }

        .location-item-badge {
          font-size: 10px;
          font-weight: 700;
          color: white;
          background: #2E7D32;
          padding: 2px 10px;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .location-loading,
        .location-empty {
          padding: 14px;
          text-align: center;
          color: #94a3b8;
          font-size: 14px;
          font-weight: 500;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Location Badge */
        .location-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 16px 4px 10px;
          border-radius: 20px;
          background: linear-gradient(135deg, #e8f0fe, #d4e4f7);
          border: 1px solid rgba(21, 101, 192, 0.25);
          color: #0a1a3a;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.3s ease;
          cursor: default;
        }

        .location-badge:hover {
          transform: scale(1.05);
        }

        .location-badge-small {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px 2px 5px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          color: white;
          margin-left: 4px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        /* Profile */
        .profile-wrapper {
          position: relative;
        }

        .profile {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 4px 14px 4px 4px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
        }

        .profile:hover {
          background: #f1f3f5;
        }

        .avatar-wrapper {
          position: relative;
          flex-shrink: 0;
        }

        .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828, #B71C1C);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 16px;
          box-shadow: 0 2px 12px rgba(198, 40, 40, 0.25);
        }

        .avatar.small {
          width: 34px;
          height: 34px;
          font-size: 14px;
        }

        .avatar-status {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 11px;
          height: 11px;
          border-radius: 50%;
          border: 2px solid white;
        }

        .avatar-status.online {
          background: #22c55e;
        }

        .profile-info {
          display: flex;
          flex-direction: column;
          line-height: 1.3;
        }

        .profile-name {
          font-size: 14px;
          font-weight: 700;
          color: #0a0a0a;
        }

        .profile-role {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }

        .role-badge {
          font-size: 10px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 6px;
          color: white;
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }

        .profile-arrow {
          color: #64748b;
          transition: transform 0.3s ease;
          flex-shrink: 0;
          font-size: 16px;
        }

        .profile:hover .profile-arrow {
          transform: rotate(180deg);
        }

        .profile-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          width: 260px;
          overflow: hidden;
          z-index: 1000;
          animation: slideDown 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(0, 0, 0, 0.04);
        }

        .dropdown-user-info {
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }

        .dropdown-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828, #B71C1C);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 18px;
          flex-shrink: 0;
        }

        .dropdown-name {
          font-size: 15px;
          font-weight: 700;
          color: #0a0a0a;
        }

        .dropdown-email {
          font-size: 12px;
          font-weight: 500;
          color: #64748b;
        }

        .dropdown-badge {
          padding: 8px 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          font-weight: 500;
          color: #1a1a1a;
          background: #f8f9fa;
          margin: 4px 12px;
          border-radius: 8px;
        }

        .dropdown-divider {
          height: 1px;
          background: rgba(0, 0, 0, 0.06);
          margin: 4px 0;
        }

        .dropdown-item {
          padding: 10px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          font-weight: 500;
          color: #0a0a0a;
        }

        .dropdown-item:hover {
          background: #f8f9fa;
        }

        .dropdown-item.logout {
          color: #C62828;
          font-weight: 600;
        }

        .dropdown-item.logout:hover {
          background: #fef3f2;
        }

        .dropdown-sub-arrow {
          margin-left: auto;
          color: #94a3b8;
          font-size: 14px;
        }

        .dropdown-submenu {
          background: #f8f9fa;
          padding: 4px 0;
        }

        .dropdown-item.sub-item {
          padding-left: 44px;
          font-size: 13px;
        }

        /* Mobile Avatar */
        .mobile-avatar {
          display: none;
          cursor: pointer;
        }

        /* Mobile Menu */
        .mobile-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(8px);
          z-index: 998;
          animation: fadeIn 0.3s ease;
        }

        .mobile-menu {
          position: fixed;
          top: 0;
          right: 0;
          width: 320px;
          max-width: 85vw;
          height: 100vh;
          background: #ffffff;
          z-index: 999;
          padding: 24px 20px;
          overflow-y: auto;
          animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: -4px 0 40px rgba(0, 0, 0, 0.1);
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .mobile-user-info {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 6px 0 20px;
        }

        .mobile-user-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828, #B71C1C);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 22px;
          flex-shrink: 0;
        }

        .mobile-user-details {
          flex: 1;
        }

        .mobile-user-name {
          font-size: 17px;
          font-weight: 700;
          color: #0a0a0a;
        }

        .mobile-user-role {
          font-size: 13px;
          font-weight: 500;
          color: #64748b;
        }

        .mobile-user-location {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #1565C0;
          margin-top: 2px;
        }

        .mobile-menu-divider {
          height: 1px;
          background: rgba(0, 0, 0, 0.06);
          margin: 16px 0;
        }

        .mobile-menu-section {
          margin: 10px 0;
        }

        .mobile-menu-label {
          font-size: 12px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          padding: 6px 0 10px;
        }

        .mobile-location-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .mobile-location-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border: none;
          background: transparent;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 15px;
          font-weight: 500;
          color: #0a0a0a;
          width: 100%;
          text-align: left;
        }

        .mobile-location-item:hover {
          background: #f1f3f5;
        }

        .mobile-location-item.active {
          background: rgba(46, 125, 50, 0.08);
          color: #1a4a2a;
          font-weight: 600;
        }

        .mobile-location-item svg {
          color: #94a3b8;
          flex-shrink: 0;
        }

        .mobile-location-item.active svg {
          color: #2E7D32;
        }

        .mobile-location-item span {
          flex: 1;
        }

        .mobile-location-check {
          color: #2E7D32;
          font-weight: 700;
          font-size: 18px;
        }

        .mobile-loading,
        .mobile-empty {
          padding: 14px;
          text-align: center;
          color: #94a3b8;
          font-size: 14px;
          font-weight: 500;
        }

        .mobile-menu-items {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .mobile-menu-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 14px;
          border: none;
          background: transparent;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 15px;
          font-weight: 500;
          color: #0a0a0a;
          width: 100%;
          text-align: left;
        }

        .mobile-menu-item:hover {
          background: #f1f3f5;
        }

        .mobile-logout-btn {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px;
          border: none;
          background: transparent;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 15px;
          font-weight: 600;
          color: #C62828;
          width: 100%;
          text-align: left;
        }

        .mobile-logout-btn:hover {
          background: #fef3f2;
        }

        /* Dialog */
        .dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(10px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease;
        }

        .dialog-container {
          background: #ffffff;
          border-radius: 24px;
          padding: 36px 28px 28px;
          max-width: 400px;
          width: 90%;
          position: relative;
          box-shadow: 0 40px 80px rgba(0, 0, 0, 0.2);
          animation: scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .password-dialog {
          max-width: 440px;
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .dialog-close {
          position: absolute;
          top: 14px;
          right: 14px;
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .dialog-close:hover {
          background: #f1f3f5;
          color: #0a0a0a;
        }

        .dialog-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .dialog-icon-wrapper {
          margin-bottom: 16px;
        }

        .password-icon-wrapper {
          margin-bottom: 12px;
        }

        .dialog-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #fef3f2;
          color: #C62828;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-icon {
          background: #e3f2fd;
          color: #1565C0;
        }

        .dialog-title {
          font-size: 20px;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 8px 0;
        }

        .dialog-message {
          font-size: 14px;
          font-weight: 500;
          color: #475569;
          line-height: 1.6;
          margin: 0 0 20px 0;
        }

        .dialog-user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f8f9fa;
          border-radius: 12px;
          width: 100%;
          margin-bottom: 24px;
        }

        .dialog-user-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828, #B71C1C);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 16px;
          flex-shrink: 0;
        }

        .dialog-user-details {
          text-align: left;
          flex: 1;
        }

        .dialog-user-name {
          font-size: 15px;
          font-weight: 700;
          color: #0a0a0a;
        }

        .dialog-user-email {
          font-size: 12px;
          font-weight: 500;
          color: #64748b;
        }

        .dialog-user-location {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
          color: #1565C0;
          margin-top: 2px;
        }

        .dialog-actions {
          display: flex;
          gap: 12px;
          width: 100%;
        }

        .dialog-btn {
          flex: 1;
          padding: 12px 20px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .dialog-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .dialog-btn-cancel {
          background: #f1f3f5;
          color: #475569;
        }

        .dialog-btn-cancel:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .dialog-btn-confirm {
          background: linear-gradient(135deg, #C62828, #B71C1C);
          color: white;
          box-shadow: 0 4px 20px rgba(198, 40, 40, 0.35);
        }

        .dialog-btn-confirm:hover:not(:disabled) {
          box-shadow: 0 8px 30px rgba(198, 40, 40, 0.45);
          transform: translateY(-1px);
        }

        .password-confirm-btn {
          background: linear-gradient(135deg, #1565C0, #0D47A1);
          box-shadow: 0 4px 16px rgba(21, 101, 192, 0.3);
        }

        .password-confirm-btn:hover:not(:disabled) {
          box-shadow: 0 8px 24px rgba(21, 101, 192, 0.4);
        }

        /* Password Form Styles */
        .password-form {
          width: 100%;
          text-align: left;
          margin-bottom: 4px;
        }

        .password-field {
          margin-bottom: 16px;
        }

        .password-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
        }

        .password-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input {
          width: 100%;
          padding: 10px 40px 10px 14px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s ease;
          background: #fafafa;
          color: #0a0a0a;
        }

        .password-input:focus {
          outline: none;
          border-color: #1565C0;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(21, 101, 192, 0.08);
        }

        .password-input.error {
          border-color: #C62828;
        }

        .password-input.error:focus {
          box-shadow: 0 0 0 4px rgba(198, 40, 40, 0.08);
        }

        .password-input:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .password-toggle {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.3s ease;
        }

        .password-toggle:hover {
          color: #0a0a0a;
        }

        .password-error {
          display: block;
          font-size: 12px;
          color: #C62828;
          margin-top: 4px;
        }

        .password-error-general {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #fef3f2;
          border: 1px solid rgba(198, 40, 40, 0.15);
          border-radius: 10px;
          color: #C62828;
          font-size: 13px;
          width: 100%;
          margin-bottom: 16px;
        }

        .password-success {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #e8f5e9;
          border: 1px solid rgba(46, 125, 50, 0.15);
          border-radius: 10px;
          color: #2E7D32;
          font-size: 13px;
          width: 100%;
          margin-bottom: 16px;
          animation: successPulse 1.5s ease;
        }

        @keyframes successPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        /* Password Strength */
        .password-strength {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .strength-bar-container {
          flex: 1;
          height: 4px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .strength-bar {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease, background-color 0.3s ease;
        }

        .strength-label {
          font-size: 11px;
          font-weight: 600;
          min-width: 60px;
          text-align: right;
        }

        /* Password Requirements */
        .password-requirements {
          margin-top: 12px;
          padding: 12px 14px;
          background: #f8f9fa;
          border-radius: 10px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }

        .requirement {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94a3b8;
          transition: color 0.3s ease;
        }

        .requirement.met {
          color: #2E7D32;
        }

        .requirement-icon {
          display: inline-block;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          border: 2px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .requirement.met .requirement-icon {
          background: #2E7D32;
          color: white;
          border-color: #2E7D32;
        }

        /* Loading Spinner */
        .loading-spinner {
          display: inline-block;
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Responsive - Tablet and Mobile */
        @media (max-width: 1024px) {
          .epos-header {
            padding: 10px 16px;
          }
          .header-tagline {
            min-width: 180px;
          }
          .clock-date {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .epos-header {
            padding: 8px 12px;
            min-height: 58px;
          }

          .mobile-menu-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .header-tagline {
            min-width: auto;
            flex: 1;
            padding: 4px 12px;
            gap: 8px;
          }

          .tagline-icon {
            font-size: 16px;
          }

          .tagline-main {
            font-size: 12px;
          }

          .tagline-sub {
            display: none;
          }

          .header-center {
            display: none;
          }

          .desktop-only {
            display: none !important;
          }

          .mobile-avatar {
            display: block;
          }

          .location-badge {
            display: none;
          }

          .header-right {
            gap: 6px;
          }

          .profile-info {
            display: none;
          }

          .profile {
            padding: 2px;
          }

          .profile-dropdown {
            right: -10px;
            width: 240px;
          }

          .dialog-container {
            padding: 28px 20px 20px;
            max-width: 360px;
          }

          .password-dialog {
            max-width: 360px;
          }

          .password-requirements {
            grid-template-columns: 1fr;
          }

          .dialog-actions {
            flex-direction: column;
          }

          /* Make location selector visible on mobile */
          .location-select-wrapper {
            display: block;
          }

          .location-select-btn {
            padding: 4px 12px 4px 8px;
            font-size: 12px;
          }

          .location-select-text {
            max-width: 70px;
            font-size: 12px;
          }

          .location-select-icon {
            width: 18px;
            height: 18px;
          }

          .location-select-icon svg {
            width: 14px;
            height: 14px;
          }

          .location-dropdown {
            right: -10px;
            min-width: 200px;
          }

          .session-warning-banner {
            font-size: 12px;
            padding: 6px 12px;
            flex-wrap: wrap;
            gap: 8px;
          }

          .session-renew-btn {
            font-size: 11px;
            padding: 3px 12px;
          }
        }

        @media (max-width: 480px) {
          .epos-header {
            padding: 6px 10px;
            min-height: 52px;
          }

          .header-tagline {
            padding: 3px 10px;
            gap: 6px;
          }

          .tagline-icon {
            font-size: 14px;
          }

          .tagline-main {
            font-size: 11px;
          }

          .mobile-menu {
            width: 300px;
            padding: 20px 16px;
          }

          .mobile-user-avatar {
            width: 48px;
            height: 48px;
            font-size: 18px;
          }

          .mobile-user-name {
            font-size: 16px;
          }

          .location-select-btn {
            padding: 3px 10px 3px 6px;
            font-size: 11px;
          }

          .location-select-text {
            font-size: 11px;
            max-width: 60px;
          }

          .location-select-icon {
            width: 16px;
            height: 16px;
          }

          .location-select-icon svg {
            width: 12px;
            height: 12px;
          }

          .avatar {
            width: 32px;
            height: 32px;
            font-size: 13px;
          }

          .avatar.small {
            width: 30px;
            height: 30px;
            font-size: 12px;
          }

          .dialog-container {
            padding: 24px 16px 16px;
            max-width: 320px;
          }

          .password-dialog {
            max-width: 320px;
          }

          .dialog-title {
            font-size: 18px;
          }

          .dialog-message {
            font-size: 13px;
          }

          .session-warning-banner {
            font-size: 11px;
            padding: 4px 10px;
          }

          .session-renew-btn {
            font-size: 10px;
            padding: 2px 10px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;